import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { cloneDeep } from 'lodash';
import BoundedInput from 'components/common/BoundedInput';
import SimulationFormFooter from 'components/common/SimulationFormFooter';

const INPUT_CLASS =
	'form-input w-20 rounded border border-indigo-300 bg-transparent px-4 py-1 text-right text-white placeholder:text-white/25';

const GlobalSimulationParams = ({
	configBoundaries = DEFAULT_CONFIG_BOUNDARIES,
	onClose,
	onStartSimulation,
	stoploss = 1,
}) => {
	const indicatorPrefix = '-simulation-global-';
	const [boundaries, setBoundaries] = useState(cloneDeep(configBoundaries));
	const [forSimulation, setForSimulation] = useState({});

	const updateBoundary = (path, value) => {
		const newBoundaries = cloneDeep(boundaries);
		const keys = path.split('.');
		let obj = newBoundaries;
		for (let i = 0; i < keys.length - 1; i++) {
			obj = obj[keys[i]];
		}
		obj[keys[keys.length - 1]] = value;
		setBoundaries(newBoundaries);
	};

	const handleSimulate = (stoplossOverride, candlesOverride) => {
		const data = {};
		const keys = Object.keys(forSimulation);
		keys.forEach(key => {
			if (forSimulation[key]) {
				if (key === 'useProfitTrailingSecurity') {
					key = 'useProfitTrailingSecurityActivatedAtPriceMove';
					data[key] = cloneDeep(boundaries[key]);
					key = 'useProfitTrailingSecurityTakeTrailingProfit';
					data[key] = cloneDeep(boundaries[key]);
				} else if (key === 'closeOnImmediateConsecutiveNrCandlesReversal') {
					data[key] = cloneDeep(boundaries[key]);
					key = 'closeOnImmediateConsecutiveNrCandlesReversalArea';
					data[key] = cloneDeep(boundaries[key]);
				} else if (key === 'preventEnterWhenPricechangeIsBeyond') {
					data[key] = cloneDeep(boundaries[key]);
					key = 'preventEnterOnExtremePriceArea';
					data[key] = cloneDeep(boundaries[key]);
				} else {
					data[key] = cloneDeep(boundaries[key]);
				}
			}
		});
		onStartSimulation(data, stoplossOverride, candlesOverride);
	};

	const switchCheckbox = key => {
		const newForSimulation = cloneDeep(forSimulation);
		newForSimulation[key] = !newForSimulation[key];
		setForSimulation(newForSimulation);
	};

	return (
		<div className="fixed left-1/2 -translate-x-1/2 text-lg inset-0 z-50 bg-black-30 px-8 w-[1200px]">
			<fieldset>
				<legend className="font-bold pt-6 mb-2 border-b w-full">Global Simulation Params:</legend>

				{/* --- Column headers (rendered once) --- */}
				<div className="flex justify-end items-center w-full mt-2 mb-1">
					<div className="flex gap-6 shrink-0 justify-end">
						<div className="flex space-x-4 w-[280px] justify-end">
							<div className="w-20 text-left text-sm text-gray-400 font-bold">min</div>
							<div className="w-20 text-left text-sm text-gray-400 font-bold">max</div>
							<div className="w-20 text-left text-sm text-gray-400 font-bold">step</div>
						</div>
						<div className="flex space-x-4 w-[280px] justify-end">
							<div className="w-20 text-left text-sm text-gray-400 font-bold">min</div>
							<div className="w-20 text-left text-sm text-gray-400 font-bold">max</div>
							<div className="w-20 text-left text-sm text-gray-400 font-bold">step</div>
						</div>
					</div>
				</div>

				{/* --- Row with 6 inputs: preventEnterWhenPricechangeIsBeyond + preventEnterOnExtremePriceArea --- */}
				<div className="flex justify-between items-center w-full gap-4 mt-2">
					<label
						htmlFor={`${indicatorPrefix}prevent-enter-when-pricechange-is-beyond`}
						className="flex items-center break-words cursor-pointer pr-4 flex-1"
					>
						<input
							type="checkbox"
							className="mr-2 scale-125"
							id={`${indicatorPrefix}prevent-enter-when-pricechange-is-beyond`}
							checked={!!forSimulation.preventEnterWhenPricechangeIsBeyond}
							onChange={switchCheckbox.bind(null, 'preventEnterWhenPricechangeIsBeyond')}
						/>
						Prevent enter when pricechange % is beyond
					</label>
					<div className="flex gap-6 shrink-0 justify-end">
						<div className="flex space-x-4 w-[280px] justify-end">
							<BoundedInput
								onChange={value => updateBoundary('preventEnterWhenPricechangeIsBeyond.min', value)}
								value={boundaries.preventEnterWhenPricechangeIsBeyond.min}
								min={0.01}
								max={9.99}
								disabled={!forSimulation.preventEnterWhenPricechangeIsBeyond}
								className={INPUT_CLASS}
							/>
							<BoundedInput
								onChange={value => updateBoundary('preventEnterWhenPricechangeIsBeyond.max', value)}
								value={boundaries.preventEnterWhenPricechangeIsBeyond.max}
								min={0.02}
								max={10}
								disabled={!forSimulation.preventEnterWhenPricechangeIsBeyond}
								className={INPUT_CLASS}
							/>
							<BoundedInput
								onChange={value => updateBoundary('preventEnterWhenPricechangeIsBeyond.step', value)}
								value={boundaries.preventEnterWhenPricechangeIsBeyond.step}
								min={0.01}
								max={0.5}
								disabled={!forSimulation.preventEnterWhenPricechangeIsBeyond}
								className={INPUT_CLASS}
							/>
						</div>
						<div className="flex space-x-4 w-[280px] justify-end">
							<BoundedInput
								onChange={value => updateBoundary('preventEnterOnExtremePriceArea.min', value)}
								value={boundaries.preventEnterOnExtremePriceArea.min}
								min={1}
								max={5}
								parseType="int"
								disabled={!forSimulation.preventEnterWhenPricechangeIsBeyond}
								className={INPUT_CLASS}
							/>
							<BoundedInput
								onChange={value => updateBoundary('preventEnterOnExtremePriceArea.max', value)}
								value={boundaries.preventEnterOnExtremePriceArea.max}
								min={2}
								max={10}
								parseType="int"
								disabled={!forSimulation.preventEnterWhenPricechangeIsBeyond}
								className={INPUT_CLASS}
							/>
							<BoundedInput
								onChange={value => updateBoundary('preventEnterOnExtremePriceArea.step', value)}
								value={boundaries.preventEnterOnExtremePriceArea.step}
								min={1}
								max={5}
								parseType="int"
								disabled={!forSimulation.preventEnterWhenPricechangeIsBeyond}
								className={INPUT_CLASS}
							/>
						</div>
					</div>
				</div>

				{/* --- preventEnterWithinCandlesAfterClosePreviousPosition --- */}
				<div className="flex justify-between items-center w-full gap-4 mt-2">
					<label
						htmlFor={`${indicatorPrefix}prevent-enter-within-candles-after-close-previous-position`}
						className="flex items-center break-words cursor-pointer pr-4 flex-1"
					>
						<input
							type="checkbox"
							className="mr-2 scale-125"
							id={`${indicatorPrefix}prevent-enter-within-candles-after-close-previous-position`}
							checked={!!forSimulation.preventEnterWithinCandlesAfterClosePreviousPosition}
							onChange={switchCheckbox.bind(null, 'preventEnterWithinCandlesAfterClosePreviousPosition')}
						/>
						Prevent enter within #candles after close previous position
					</label>
					<div className="flex space-x-4 w-[280px] shrink-0 justify-end">
						<BoundedInput
							onChange={value => updateBoundary('preventEnterWithinCandlesAfterClosePreviousPosition.min', value)}
							value={boundaries.preventEnterWithinCandlesAfterClosePreviousPosition.min}
							min={1}
							max={99}
							parseType="int"
							disabled={!forSimulation.preventEnterWithinCandlesAfterClosePreviousPosition}
							className={INPUT_CLASS}
						/>
						<BoundedInput
							onChange={value => updateBoundary('preventEnterWithinCandlesAfterClosePreviousPosition.max', value)}
							value={boundaries.preventEnterWithinCandlesAfterClosePreviousPosition.max}
							min={2}
							max={100}
							parseType="int"
							disabled={!forSimulation.preventEnterWithinCandlesAfterClosePreviousPosition}
							className={INPUT_CLASS}
						/>
						<BoundedInput
							onChange={value => updateBoundary('preventEnterWithinCandlesAfterClosePreviousPosition.step', value)}
							value={boundaries.preventEnterWithinCandlesAfterClosePreviousPosition.step}
							min={1}
							max={10}
							parseType="int"
							disabled={!forSimulation.preventEnterWithinCandlesAfterClosePreviousPosition}
							className={INPUT_CLASS}
						/>
					</div>
				</div>

				{/* --- stoploss --- */}
				<div className="flex justify-between items-center w-full gap-4 mt-2">
					<label
						htmlFor={`${indicatorPrefix}stoploss`}
						className="flex items-center break-words cursor-pointer pr-4 flex-1"
					>
						<input
							type="checkbox"
							className="mr-2 scale-125"
							id={`${indicatorPrefix}stoploss`}
							checked={!!forSimulation.stoploss}
							onChange={switchCheckbox.bind(null, 'stoploss')}
						/>
						Stoploss % (real price)
					</label>
					<div className="flex space-x-4 w-[280px] shrink-0 justify-end">
						<BoundedInput
							onChange={value => updateBoundary('stoploss.min', value)}
							value={boundaries.stoploss.min}
							min={0.01}
							max={9.99}
							disabled={!forSimulation.stoploss}
							className={INPUT_CLASS}
						/>
						<BoundedInput
							onChange={value => updateBoundary('stoploss.max', value)}
							value={boundaries.stoploss.max}
							min={0.02}
							max={50}
							disabled={!forSimulation.stoploss}
							className={INPUT_CLASS}
						/>
						<BoundedInput
							onChange={value => updateBoundary('stoploss.step', value)}
							value={boundaries.stoploss.step}
							min={0.01}
							max={0.5}
							disabled={!forSimulation.stoploss}
							className={INPUT_CLASS}
						/>
					</div>
				</div>

				{/* --- Row with 6 inputs: closeOnImmediateConsecutiveNrCandlesReversal + Area --- */}
				<div className="flex justify-between items-center w-full gap-4 mt-2">
					<label
						htmlFor={`${indicatorPrefix}close-on-immediate-consecutive-nr-candles-reversal`}
						className="flex items-center break-words cursor-pointer pr-4 flex-1"
					>
						<input
							type="checkbox"
							className="mr-2 scale-125"
							id={`${indicatorPrefix}close-on-immediate-consecutive-nr-candles-reversal`}
							checked={!!forSimulation.closeOnImmediateConsecutiveNrCandlesReversal}
							onChange={switchCheckbox.bind(null, 'closeOnImmediateConsecutiveNrCandlesReversal')}
						/>
						Close on immediate consecutive #candles reversal
					</label>
					<div className="flex gap-6 shrink-0 justify-end">
						<div className="flex space-x-4 w-[280px] justify-end">
							<BoundedInput
								onChange={value => updateBoundary('closeOnImmediateConsecutiveNrCandlesReversal.min', value)}
								value={boundaries.closeOnImmediateConsecutiveNrCandlesReversal.min}
								min={1}
								max={99}
								parseType="int"
								disabled={!forSimulation.closeOnImmediateConsecutiveNrCandlesReversal}
								className={INPUT_CLASS}
							/>
							<BoundedInput
								onChange={value => updateBoundary('closeOnImmediateConsecutiveNrCandlesReversal.max', value)}
								value={boundaries.closeOnImmediateConsecutiveNrCandlesReversal.max}
								min={2}
								max={100}
								parseType="int"
								disabled={!forSimulation.closeOnImmediateConsecutiveNrCandlesReversal}
								className={INPUT_CLASS}
							/>
							<BoundedInput
								onChange={value => updateBoundary('closeOnImmediateConsecutiveNrCandlesReversal.step', value)}
								value={boundaries.closeOnImmediateConsecutiveNrCandlesReversal.step}
								min={1}
								max={25}
								parseType="int"
								disabled={!forSimulation.closeOnImmediateConsecutiveNrCandlesReversal}
								className={INPUT_CLASS}
							/>
						</div>
						<div className="flex space-x-4 w-[280px] justify-end">
							<BoundedInput
								onChange={value => updateBoundary('closeOnImmediateConsecutiveNrCandlesReversalArea.min', value)}
								value={boundaries.closeOnImmediateConsecutiveNrCandlesReversalArea.min}
								min={1}
								max={99}
								parseType="int"
								disabled={!forSimulation.closeOnImmediateConsecutiveNrCandlesReversal}
								className={INPUT_CLASS}
							/>
							<BoundedInput
								onChange={value => updateBoundary('closeOnImmediateConsecutiveNrCandlesReversalArea.max', value)}
								value={boundaries.closeOnImmediateConsecutiveNrCandlesReversalArea.max}
								min={2}
								max={100}
								parseType="int"
								disabled={!forSimulation.closeOnImmediateConsecutiveNrCandlesReversal}
								className={INPUT_CLASS}
							/>
							<BoundedInput
								onChange={value => updateBoundary('closeOnImmediateConsecutiveNrCandlesReversalArea.step', value)}
								value={boundaries.closeOnImmediateConsecutiveNrCandlesReversalArea.step}
								min={1}
								max={25}
								parseType="int"
								disabled={!forSimulation.closeOnImmediateConsecutiveNrCandlesReversal}
								className={INPUT_CLASS}
							/>
						</div>
					</div>
				</div>

				{/* --- standalone checkbox: stoplossOnlyOnTwoSequentCandles --- */}
				<label
					htmlFor={`${indicatorPrefix}stoploss-only-on-two-sequent-candles`}
					className="flex items-center cursor-pointer mt-4"
				>
					<input
						type="checkbox"
						className="mr-2 scale-125"
						id={`${indicatorPrefix}stoploss-only-on-two-sequent-candles`}
						checked={!!forSimulation.stoplossOnlyOnTwoSequentCandles}
						onChange={switchCheckbox.bind(null, 'stoplossOnlyOnTwoSequentCandles')}
					/>
					Stoploss only on 2 sequent candles
				</label>

				{/* --- standalone checkbox: closeOnPriceReversalToOriginalCandleOpenPrice --- */}
				<label
					htmlFor={`${indicatorPrefix}close-on-price-reversal-to-original-candle-open-price`}
					className="flex items-center cursor-pointer mt-2"
				>
					<input
						type="checkbox"
						className="mr-2 scale-125"
						id={`${indicatorPrefix}close-on-price-reversal-to-original-candle-open-price`}
						checked={!!forSimulation.closeOnPriceReversalToOriginalCandleOpenPrice}
						onChange={switchCheckbox.bind(null, 'closeOnPriceReversalToOriginalCandleOpenPrice')}
					/>
					Close on price reversal to original Candle Open price (real price)
				</label>

				{/* --- Row with 6 inputs: useProfitTrailingSecurity (ActivatedAtPriceMove + TakeTrailingProfit) --- */}
				<div className="flex justify-between items-center w-full gap-4 mt-2">
					<label
						htmlFor={`${indicatorPrefix}use-profit-trailing-security`}
						className="flex items-center break-words cursor-pointer pr-4 flex-1"
					>
						<input
							type="checkbox"
							className="mr-2 scale-125"
							id={`${indicatorPrefix}use-profit-trailing-security`}
							checked={!!forSimulation.useProfitTrailingSecurity}
							onChange={switchCheckbox.bind(null, 'useProfitTrailingSecurity')}
						/>
						Use Profit Trailing security
					</label>
					<div className="flex gap-6 shrink-0 justify-end">
						<div className="flex space-x-4 w-[280px] justify-end">
							<BoundedInput
								onChange={value => updateBoundary('useProfitTrailingSecurityActivatedAtPriceMove.min', value)}
								value={boundaries.useProfitTrailingSecurityActivatedAtPriceMove.min}
								min={0.1}
								max={4.99}
								disabled={!forSimulation.useProfitTrailingSecurity}
								className={INPUT_CLASS}
							/>
							<BoundedInput
								onChange={value => updateBoundary('useProfitTrailingSecurityActivatedAtPriceMove.max', value)}
								value={boundaries.useProfitTrailingSecurityActivatedAtPriceMove.max}
								min={0.2}
								max={5}
								disabled={!forSimulation.useProfitTrailingSecurity}
								className={INPUT_CLASS}
							/>
							<BoundedInput
								onChange={value => updateBoundary('useProfitTrailingSecurityActivatedAtPriceMove.step', value)}
								value={boundaries.useProfitTrailingSecurityActivatedAtPriceMove.step}
								min={0.1}
								max={1}
								disabled={!forSimulation.useProfitTrailingSecurity}
								className={INPUT_CLASS}
							/>
						</div>
						<div className="flex space-x-4 w-[280px] justify-end">
							<BoundedInput
								onChange={value => updateBoundary('useProfitTrailingSecurityTakeTrailingProfit.min', value)}
								value={boundaries.useProfitTrailingSecurityTakeTrailingProfit.min}
								min={1}
								max={99}
								parseType="int"
								disabled={!forSimulation.useProfitTrailingSecurity}
								className={INPUT_CLASS}
							/>
							<BoundedInput
								onChange={value => updateBoundary('useProfitTrailingSecurityTakeTrailingProfit.max', value)}
								value={boundaries.useProfitTrailingSecurityTakeTrailingProfit.max}
								min={2}
								max={100}
								parseType="int"
								disabled={!forSimulation.useProfitTrailingSecurity}
								className={INPUT_CLASS}
							/>
							<BoundedInput
								onChange={value => updateBoundary('useProfitTrailingSecurityTakeTrailingProfit.step', value)}
								value={boundaries.useProfitTrailingSecurityTakeTrailingProfit.step}
								min={1}
								max={10}
								parseType="int"
								disabled={!forSimulation.useProfitTrailingSecurity}
								className={INPUT_CLASS}
							/>
						</div>
					</div>
				</div>

				{/* --- standalone checkbox: preventCloseOnProfitCandle --- */}
				<label
					htmlFor={`${indicatorPrefix}prevent-close-on-profit-candle`}
					className="flex items-center cursor-pointer mt-4"
				>
					<input
						type="checkbox"
						className="mr-2 scale-125"
						id={`${indicatorPrefix}prevent-close-on-profit-candle`}
						checked={!!forSimulation.preventCloseOnProfitCandle}
						onChange={switchCheckbox.bind(null, 'preventCloseOnProfitCandle')}
					/>
					Prevent close when candle is in trade-direction (profit trailing)
				</label>

				{/* --- preventCloseWithinFirstNrCandles --- */}
				<div className="flex justify-between items-center w-full gap-4 mt-2">
					<label
						htmlFor={`${indicatorPrefix}prevent-close-within-first-nr-candles`}
						className="flex items-center break-words cursor-pointer pr-4 flex-1"
					>
						<input
							type="checkbox"
							className="mr-2 scale-125"
							id={`${indicatorPrefix}prevent-close-within-first-nr-candles`}
							checked={!!forSimulation.preventCloseWithinFirstNrCandles}
							onChange={switchCheckbox.bind(null, 'preventCloseWithinFirstNrCandles')}
						/>
						Prevent close within first #candles
					</label>
					<div className="flex space-x-4 w-[280px] shrink-0 justify-end">
						<BoundedInput
							onChange={value => updateBoundary('preventCloseWithinFirstNrCandles.min', value)}
							value={boundaries.preventCloseWithinFirstNrCandles.min}
							min={1}
							max={19}
							parseType="int"
							disabled={!forSimulation.preventCloseWithinFirstNrCandles}
							className={INPUT_CLASS}
						/>
						<BoundedInput
							onChange={value => updateBoundary('preventCloseWithinFirstNrCandles.max', value)}
							value={boundaries.preventCloseWithinFirstNrCandles.max}
							min={2}
							max={20}
							parseType="int"
							disabled={!forSimulation.preventCloseWithinFirstNrCandles}
							className={INPUT_CLASS}
						/>
						<BoundedInput
							onChange={value => updateBoundary('preventCloseWithinFirstNrCandles.step', value)}
							value={boundaries.preventCloseWithinFirstNrCandles.step}
							min={1}
							max={5}
							parseType="int"
							disabled={!forSimulation.preventCloseWithinFirstNrCandles}
							className={INPUT_CLASS}
						/>
					</div>
				</div>

				{/* --- standalone checkbox: preventCloseWhenPriceLost --- */}
				<label
					htmlFor={`${indicatorPrefix}prevent-close-when-price-lost`}
					className="flex items-center cursor-pointer mt-4"
				>
					<input
						type="checkbox"
						className="mr-2 scale-125"
						id={`${indicatorPrefix}prevent-close-when-price-lost`}
						checked={!!forSimulation.preventCloseWhenPriceLost}
						onChange={switchCheckbox.bind(null, 'preventCloseWhenPriceLost')}
					/>
					Prevent close when price lost (candles)
				</label>

				{/* --- minProfit --- */}
				<div className="flex justify-between items-center w-full gap-4 mt-2">
					<label
						htmlFor={`${indicatorPrefix}min-profit`}
						className="flex items-center break-words cursor-pointer pr-4 flex-1"
					>
						<input
							type="checkbox"
							className="mr-2 scale-125"
							id={`${indicatorPrefix}min-profit`}
							checked={!!forSimulation.minProfit}
							onChange={switchCheckbox.bind(null, 'minProfit')}
						/>
						Minimum Profit when Prevent close when price lost
					</label>
					<div className="flex space-x-4 w-[280px] shrink-0 justify-end">
						<BoundedInput
							onChange={value => updateBoundary('minProfit.min', value)}
							value={boundaries.minProfit.min}
							min={0}
							max={100}
							disabled={!forSimulation.minProfit}
							className={INPUT_CLASS}
						/>
						<BoundedInput
							onChange={value => updateBoundary('minProfit.max', value)}
							value={boundaries.minProfit.max}
							min={1}
							max={100}
							disabled={!forSimulation.minProfit}
							className={INPUT_CLASS}
						/>
						<BoundedInput
							onChange={value => updateBoundary('minProfit.step', value)}
							value={boundaries.minProfit.step}
							min={0.1}
							max={50}
							disabled={!forSimulation.minProfit}
							className={INPUT_CLASS}
						/>
					</div>
				</div>

				{/* --- preventCloseWithinFirstNrCandlesOnlyDuringNrCandles --- */}
				<div className="flex justify-between items-center w-full gap-4 mt-2">
					<label
						htmlFor={`${indicatorPrefix}prevent-close-within-first-nr-candles-only-during-nr-candles`}
						className="flex items-center break-words cursor-pointer pr-4 flex-1"
					>
						<input
							type="checkbox"
							className="mr-2 scale-125"
							id={`${indicatorPrefix}prevent-close-within-first-nr-candles-only-during-nr-candles`}
							checked={!!forSimulation.preventCloseWithinFirstNrCandlesOnlyDuringNrCandles}
							onChange={switchCheckbox.bind(null, 'preventCloseWithinFirstNrCandlesOnlyDuringNrCandles')}
						/>
						Prevent close within first #candles ONLY during #candles
					</label>
					<div className="flex space-x-4 w-[280px] shrink-0 justify-end">
						<BoundedInput
							onChange={value => updateBoundary('preventCloseWithinFirstNrCandlesOnlyDuringNrCandles.min', value)}
							value={boundaries.preventCloseWithinFirstNrCandlesOnlyDuringNrCandles.min}
							min={1}
							max={499}
							parseType="int"
							disabled={!forSimulation.preventCloseWithinFirstNrCandlesOnlyDuringNrCandles}
							className={INPUT_CLASS}
						/>
						<BoundedInput
							onChange={value => updateBoundary('preventCloseWithinFirstNrCandlesOnlyDuringNrCandles.max', value)}
							value={boundaries.preventCloseWithinFirstNrCandlesOnlyDuringNrCandles.max}
							min={2}
							max={500}
							parseType="int"
							disabled={!forSimulation.preventCloseWithinFirstNrCandlesOnlyDuringNrCandles}
							className={INPUT_CLASS}
						/>
						<BoundedInput
							onChange={value => updateBoundary('preventCloseWithinFirstNrCandlesOnlyDuringNrCandles.step', value)}
							value={boundaries.preventCloseWithinFirstNrCandlesOnlyDuringNrCandles.step}
							min={1}
							max={50}
							parseType="int"
							disabled={!forSimulation.preventCloseWithinFirstNrCandlesOnlyDuringNrCandles}
							className={INPUT_CLASS}
						/>
					</div>
				</div>

				{/* --- standalone checkbox: preventCloseOnPositionInOtherDirection --- */}
				<label
					htmlFor={`${indicatorPrefix}prevent-close-on-position-in-other-direction`}
					className="flex items-center cursor-pointer mt-4"
				>
					<input
						type="checkbox"
						className="mr-2 scale-125"
						id={`${indicatorPrefix}prevent-close-on-position-in-other-direction`}
						checked={!!forSimulation.preventCloseOnPositionInOtherDirection}
						onChange={switchCheckbox.bind(null, 'preventCloseOnPositionInOtherDirection')}
					/>
					Prevent close on position in other direction
				</label>
			</fieldset>
			<SimulationFormFooter
				stoploss={stoploss}
				stoplossMax={50}
				onClose={onClose}
				onStart={handleSimulate}
				startDisabled={!Object.values(forSimulation).some(v => v)}
			/>
		</div>
	);
};

const DEFAULT_CONFIG_BOUNDARIES = {
	preventEnterWhenPricechangeIsBeyond: {
		type: 'number',
		min: 0.05,
		max: 1,
		step: 0.05,
	},
	preventEnterOnExtremePriceArea: {
		type: 'number',
		min: 1,
		max: 5,
		step: 1,
	},
	preventEnterWithinCandlesAfterClosePreviousPosition: {
		type: 'number',
		min: 5,
		max: 50,
		step: 1,
	},
	stoploss: {
		type: 'number',
		min: 0.5,
		max: 2,
		step: 0.1,
	},
	closeOnImmediateConsecutiveNrCandlesReversal: {
		type: 'number',
		min: 3,
		max: 8,
		step: 1,
	},
	closeOnImmediateConsecutiveNrCandlesReversalArea: {
		type: 'number',
		min: 0,
		max: 20,
		step: 1,
	},
	stoplossOnlyOnTwoSequentCandles: 'boolean',
	closeOnPriceReversalToOriginalCandleOpenPrice: 'boolean',
	useProfitTrailingSecurityActivatedAtPriceMove: {
		type: 'number',
		min: 0.5,
		max: 2,
		step: 0.1,
	},
	useProfitTrailingSecurityTakeTrailingProfit: {
		type: 'number',
		min: 0,
		max: 40,
		step: 5,
	},
	preventCloseWithinFirstNrCandles: {
		type: 'number',
		min: 1,
		max: 10,
		step: 1,
	},
	preventCloseWhenPriceLost: 'boolean',
	preventCloseOnProfitCandle: 'boolean',
	preventCloseWithinFirstNrCandlesOnlyDuringNrCandles: {
		type: 'number',
		min: 20,
		max: 100,
		step: 5,
	},
	minProfit: {
		type: 'number',
		min: 0,
		max: 5,
		step: 0.1,
	},
	preventCloseOnPositionInOtherDirection: 'boolean',
};

GlobalSimulationParams.propTypes = {
	onClose: PropTypes.func.isRequired,
	onStartSimulation: PropTypes.func.isRequired,
};

export default GlobalSimulationParams;
